import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FileService } from '../file/file.service';
import { SwalOption, SwalService } from '../swal/swal.service';
import { Socket } from 'ngx-socket-io';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  API_SERVER: string = environment.baseApiUrl;

  generateReportProgressSub: Subscription;

  constructor(
    private httpClient: HttpClient,
    private swalService: SwalService,
    private fileService: FileService,
    private spinner: NgxSpinnerService,
    private _t: TranslateService,
    private socket: Socket
  ) {}

  private extractData(res: Response) {
    const body = res;
    return body || {};
  }

  findAllParent(): Observable<any> {
    return this.httpClient
      .get(
        this.API_SERVER +
          'location?filtered[0][key]=parent&filtered[0][val]=null'
      )
      .pipe(map(this.extractData));
  }

  findOwn(): Observable<any> {
    return this.httpClient
      .get(this.API_SERVER + 'location/own')
      .pipe(map(this.extractData));
  }

  findAll(params?): Observable<any> {
    let Params = new HttpParams();
    if (params && Object.keys(params).length > 0) {
      params.forEach((elem) => {
        Params = Params.append(elem.key, elem.val);
      });
    }
    return this.httpClient
      .get(this.API_SERVER + 'location', { params: Params })
      .pipe(map(this.extractData));
  }

  getSummaries(): Observable<any> {
    return this.httpClient
      .get(this.API_SERVER + 'location/summary')
      .pipe(map(this.extractData));
  }

  getMembers(id): Observable<any> {
    return this.httpClient
      .get(this.API_SERVER + `location/members/${id}`)
      .pipe(map(this.extractData));
  }

  findOne(id): Observable<ILocation> {
    return this.httpClient
      .get(this.API_SERVER + `location/${id}`)
      .pipe(map(this.extractData));
  }

  findDescendants(id): Observable<any> {
    return this.httpClient
      .get(this.API_SERVER + `location/descendants/${id}`)
      .pipe(map(this.extractData));
  }

  create(data): Observable<any> {
    return this.httpClient
      .post(this.API_SERVER + 'location', data)
      .pipe(map(this.extractData));
  }

  addMember(id, data): Observable<any> {
    return this.httpClient
      .post(this.API_SERVER + `location/add-member/${id}`, data)
      .pipe(map(this.extractData));
  }

  removeMember(id, data): Observable<any> {
    return this.httpClient
      .post(this.API_SERVER + `location/remove-member/${id}`, data)
      .pipe(map(this.extractData));
  }

  addReviewer(id, data): Observable<any> {
    return this.httpClient
      .post(this.API_SERVER + `location/add-reviewer/${id}`, data)
      .pipe(map(this.extractData));
  }

  removeReviewer(id, data): Observable<any> {
    return this.httpClient
      .post(this.API_SERVER + `location/remove-reviewer/${id}`, data)
      .pipe(map(this.extractData));
  }

  addSubmitter(id, data): Observable<any> {
    return this.httpClient
      .post(this.API_SERVER + `location/add-submitter/${id}`, data)
      .pipe(map(this.extractData));
  }

  removeSubmitter(id, data): Observable<any> {
    return this.httpClient
      .post(this.API_SERVER + `location/remove-submitter/${id}`, data)
      .pipe(map(this.extractData));
  }

  update(id, data): Observable<any> {
    return this.httpClient
      .put(this.API_SERVER + `location/${id}`, data)
      .pipe(map(this.extractData));
  }

  updateFaq(id, data): Observable<any> {
    return this.httpClient
      .put(this.API_SERVER + `location/set_faq/${id}`, data)
      .pipe(map(this.extractData));
  }

  remove(id): Observable<any> {
    return this.httpClient
      .delete(this.API_SERVER + `location/${id}`)
      .pipe(map(this.extractData));
  }

  restore(id): Observable<any> {
    return this.httpClient
      .delete(this.API_SERVER + `location/restore/${id}`)
      .pipe(map(this.extractData));
  }

  /**
   * Make requests to the server to generate report for the location with ID in the arg
   * @param id Location ID to generate the report from
   * @param filters Optional filters
   * @param isSorted Optional property to preserve the sorting from the request
   */
  getReport(id: string, filters?: any, isSorted?: boolean): Observable<any> {
    // Refer to: https://angular.io/guide/http#requesting-data-from-a-server/
    const httpOpt: { responseType: 'text' } = {
      responseType: 'text',
    };

    const payload: {
      apiUrl: string;
      /** Annotation filters */
      filters?: {
        /**
         * Possible keys are:
         * - 'location_component'
         * - 'severity_type'
         * - 'annotation_type'
         */
        [key: string]: string[];
      };
      sorted?: boolean;
    } = {
      /** This is required by server to properly generate email link */
      apiUrl: environment.baseApiUrl,
    };

    if (filters) {
      payload['filters'] = filters;
      if (isSorted) payload['sorted'] = true;
    }

    return this.httpClient.put(
      this.API_SERVER + `location/report/${id}`,
      payload,
      httpOpt
    );
  }

  /**
   * This works in tandem with fileService.simulateDownloadClick
   */
  genDownloadUrl(id: string) {
    // return this.httpClient.get(this.API_SERVER + `location/report/download/${id}`)
    return this.API_SERVER + `location/report/download/${id}`;
  }

  /**
   * Generates report, includes swal and spinner on loading
   * @param id Location ID
   * @param filters Annotation filters
   */
  async generateReport(
    id: string,
    filters?: {
      location_component?: string[];
      severity_type?: string[];
      annotation_type?: string[];
      annotationIds?: string[];
    },
    isSorted?: boolean
  ) {
    if (!id) return;

    this.spinner.show();
    try {
      setTimeout(() => {
        let spinnerLoadingTextElem =
          document.getElementsByClassName('loading-text')[0];
        spinnerLoadingTextElem.classList.add('text-light');

        this.generateReportProgressSub = this.socket
          .fromEvent(`generateReportProgress-${id}`)
          .subscribe((res) => {
            spinnerLoadingTextElem.innerHTML =
              ((res['cached'] / res['balance']) * 100).toFixed(0) +
              '% ' +
              `(${res['cached']} / ${res['balance']} pages)` +
              (res['cached'] === res['balance']
                ? '(Compiling & Generating PDF)'
                : '');
          });
      }, 500);

      const res = await this.getReport(id, filters, isSorted).toPromise();

      if (!res.includes('http')) {
        const swalOpt: SwalOption = {
          text: this._t.instant('We will email and notify you once its ready'),
          title: this._t.instant('Report is being generated'),
          showCancelButton: false,
          confirmButtonText: this._t.instant('Ok'),
        };
        this.swalService.confirmDialog((swalRes) => {}, swalOpt);
      } else {
        // this.fileService.openNewTab(res)
        /** Response contains `"` symbols */
        const url = res.split('"').find((ele) => !!ele);

        const swalOpt: SwalOption = {
          text: this._t.instant('Would you like to download?'),
          title: this._t.instant('Report generated'),
          showCancelButton: true,
          confirmButtonText: this._t.instant('Ok'),
        };

        this.swalService.confirmDialog((swalRes) => {
          // const newUrl = this.genDownloadUrl(id)
          this.fileService.simulateDownloadClick(url, '.pdf');
        }, swalOpt);
      }
    } catch (err) {
      console.error('summary.generateReport().Err: ', err);
    } finally {
      this.generateReportProgressSub.unsubscribe();
      this.spinner.hide();
    }
  }

  setPinnedMapLayer(
    location_id: string,
    pinned_map_layer_type: string[]
  ): Observable<any> {
    return this.httpClient
      .post(this.API_SERVER + `location/${location_id}`, {
        pinned_map_layer_type,
      })
      .pipe(map(this.extractData));
  }
}

export interface ILocation {
  status?: string;
  location_components?: string[];
  location_thumbnail?: string;
  files?: any[];
  annotations?: string[];
  coordinate?: string[];
  name?: string;
  description?: string;
  address?: string;
  update_at?: string;
  created_at?: string;
  id?: string;
  parent?: any;
  descendants?: any[];
  children?: any[];
  map_layer?: any[];
  map_layer_ortho?: string;
  map_layer_ortho_all?: {
    id: string;
    external_url: string;
    layer_date: string;
    layer_type: string;
  }[];
  map_layer_3d?: string;
  map_layer_thermal?: string;
  map_layer_3d_mesh?: string;
  map_layer_dsm?: string;
  map_layer_embed?: string[];
  map_layer_geojson?: string[];
  map_layer_facade?: string;
  visibility?: string;
  generic_type_template?: any;
  members?: any[];
  groups?: any[];
  pinned_map_layer_type?: any[];
  default_zoom_level?: number;
  ctqrs_enabled?: boolean;
  ctqrs_widget_data?: {
    cost: {
      budget_cumulative_all: number;
      actual_cumulative_all: number;
    };
    time: {
      latest_planned: number;
      latest_actual: number;
      latest_delta: number;
    };
    resource: {
      budget_cumulative_all: number;
      actual_cumulative_all: number;
    };
    quality: {
      issues: number;
      rectified: number;
    };
    safety: {
      LTI: number;
      ZETO: number;
      minor: number;
    };
  };
  additional_data_json?: string;
  show_annotation_marker?: boolean;
  show_review_module?: boolean;
  faq_enabled?: boolean;
  faq?: string;
  reviewers?: any[];
  submitter?: any[];
  endorsement_status?: string;
}
